import React, { useEffect, useRef } from 'react';
import { FaShieldAlt, FaLock, FaUserSecret, FaCookieBite, FaDatabase, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt } from 'react-icons/fa';

const PrivacyPolicy = () => {
  const headerRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === headerRef.current) {
              entry.target.classList.add('animate-fade-down');
            }
            if (entry.target === contentRef.current) {
              entry.target.classList.add('animate-fade-up');
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) observer.observe(headerRef.current);
    if (contentRef.current) observer.observe(contentRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-br from-gray-50 via-white to-red-50 py-16 md:py-24">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-72 h-72 bg-red-400 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-300 rounded-full filter blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-red-200 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10 max-w-5xl">
        {/* Header Section */}
        <div
          ref={headerRef}
          className="text-center mb-12 opacity-0 transition-all duration-700"
          style={{ transform: 'translateY(-20px)' }}
        >
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-600 px-4 py-1.5 rounded-full mb-6">
            <FaShieldAlt className="text-sm" />
            <span className="text-xs font-semibold uppercase tracking-wider">Legal & Compliance</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            <span className="text-gray-800">Privacy </span>
            <span className="text-red-600 relative inline-block">
              Policy
              <span className="absolute bottom-0 left-0 w-full h-1 bg-red-500 rounded-full"></span>
            </span>
          </h1>
          <div className="w-24 h-1 bg-red-500 rounded-full mx-auto mb-6"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Last Updated: January 1, 2025
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Ambris Bike & Car Service Center | Sathawa, Sarnath, Varanasi - 221007
          </p>
        </div>

        {/* Main Content */}
        <div
          ref={contentRef}
          className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 md:p-10 border border-red-200 shadow-xl opacity-0 transition-all duration-700"
          style={{ transform: 'translateY(30px)' }}
        >
          <div className="prose prose-gray max-w-none">
            {/* Contact Info Card - Prominent at top */}
            <div className="mb-8 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-5 border border-red-200">
              <h3 className="text-red-600 font-semibold mb-3 flex items-center gap-2">
                <FaEnvelope className="text-sm" />
                Our Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-3">
                  <FaMapMarkerAlt className="text-red-500 text-lg" />
                  <div>
                    <p className="text-xs text-gray-500">Address</p>
                    <p className="text-sm text-gray-800 font-medium">Sathawa, Sarnath, Varanasi,<br />Uttar Pradesh - 221007</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FaPhoneAlt className="text-red-500 text-lg" />
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="text-sm text-gray-800 font-medium">+91 8081321406</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FaEnvelope className="text-red-500 text-lg" />
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm text-gray-800 font-medium">info@ambrisservice.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Introduction */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaUserSecret className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">Introduction</h2>
              </div>
              <p className="text-gray-700 leading-relaxed">
                At <strong>Ambris Bike & Car Service Center</strong> ("we," "our," or "us"), your privacy is a top priority. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our 
                website or use our services. Please read this policy carefully. By accessing our website or using our services, 
                you agree to the terms of this Privacy Policy.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaDatabase className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">Information We Collect</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-3">We may collect the following types of information:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Personal Information:</strong> Name, email address, phone number, and vehicle details when you contact us or book a service.</li>
                <li><strong>Location Data:</strong> If you use the "Get Directions" button, your device's location data (with your consent) to provide route information to our address at <strong>Sathawa, Sarnath, Varanasi - 221007</strong>.</li>
                <li><strong>Communication Data:</strong> When you email us at <strong>info@ambrisservice.com</strong> or call us at <strong>+91 8081321406</strong>, we may retain records of those communications.</li>
                <li><strong>Usage Data:</strong> Information about how you interact with our website (pages visited, time spent, etc.).</li>
                <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers.</li>
              </ul>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaEnvelope className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">How We Use Your Information</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-3">We use the collected information for:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Providing and maintaining our vehicle servicing and repair services at our Sarnath, Varanasi location.</li>
                <li>Responding to your inquiries via phone (<strong>+91 8081321406</strong>) or email (<strong>info@ambrisservice.com</strong>).</li>
                <li>Sending service reminders, appointment confirmations, and follow-up communications.</li>
                <li>Improving our website, services, and customer experience.</li>
                <li>Processing payments and managing bookings (if applicable).</li>
                <li>Complying with legal obligations and resolving disputes.</li>
              </ul>
            </div>

            {/* Cookies & Tracking */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaCookieBite className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">Cookies & Tracking Technologies</h2>
              </div>
              <p className="text-gray-700 leading-relaxed">
                We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, 
                and personalize content. You can control cookie preferences through your browser settings. However, disabling 
                cookies may affect certain website functionalities.
              </p>
            </div>

            {/* Information Sharing */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaLock className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">Information Sharing & Disclosure</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-3">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Service Providers:</strong> With trusted third-party vendors who assist in website hosting, analytics, or communication services (under strict confidentiality).</li>
                <li><strong>Legal Requirements:</strong> If required by law, court order, or government regulation.</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or asset sale (with notice to you).</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information.</li>
              </ul>
            </div>

            {/* Data Security */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaShieldAlt className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">Data Security</h2>
              </div>
              <p className="text-gray-700 leading-relaxed">
                We implement industry-standard security measures (including SSL encryption, firewalls, and secure data storage) 
                to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, 
                no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect 
                your data, we cannot guarantee absolute security.
              </p>
            </div>

            {/* Your Rights */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Your Rights & Choices</h2>
              <p className="text-gray-700 leading-relaxed mb-3">Depending on your location, you may have the following rights:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
                <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal exceptions).</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications via the "unsubscribe" link in our emails.</li>
                <li><strong>Data Portability:</strong> Request transfer of your data to another service provider.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                To exercise these rights, please contact us using the information below.
              </p>
            </div>

            {/* Third-Party Links */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Third-Party Links</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website may contain links to third-party websites (e.g., Google Maps for directions to our Sarnath location). 
                We are not responsible for the privacy practices or content of these external sites. We encourage you to review 
                their privacy policies before providing any personal information.
              </p>
            </div>

            {/* Children's Privacy */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are not directed to individuals under the age of 13. We do not knowingly collect personal information 
                from children. If you believe a child has provided us with personal data, please contact us immediately, and we 
                will take steps to delete such information.
              </p>
            </div>

            {/* Grievance Officer - Required for India */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Grievance Officer (As per Indian Laws)</h2>
              <p className="text-gray-700 leading-relaxed">
                In compliance with the Information Technology Act, 2000 and its rules, the Grievance Officer for 
                Ambris Bike & Car Service Center is:
              </p>
              <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="font-semibold text-gray-800">Name: Proprietor / Owner</p>
                <p className="text-gray-700">Ambris Bike & Car Service Center</p>
                <p className="text-gray-700">Sathawa, Sarnath, Varanasi, Uttar Pradesh - 221007</p>
                <p className="text-gray-700">Phone: +91 8081321406</p>
                <p className="text-gray-700">Email: info@ambrisservice.com</p>
                <p className="text-sm text-gray-500 mt-2">Response Time: Within 48 hours of receiving the grievance</p>
              </div>
            </div>

            {/* Policy Updates */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Updates to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. 
                We will notify you of any material changes by posting the new Privacy Policy on this page and updating the 
                "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </div>

            {/* Contact Us */}
            <div className="mt-6 bg-red-50 rounded-xl p-6 border border-red-200">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FaEnvelope className="text-red-500" />
                Contact Us
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
                please contact us at:
              </p>
              <div className="space-y-2">
                <p className="flex items-center gap-3 text-gray-700">
                  <FaMapMarkerAlt className="text-red-500" />
                  <strong>Address:</strong> Sathawa, Sarnath, Varanasi, Uttar Pradesh - 221007
                </p>
                <p className="flex items-center gap-3 text-gray-700">
                  <FaPhoneAlt className="text-red-500" />
                  <strong>Phone:</strong> +91 8081321406
                </p>
                <p className="flex items-center gap-3 text-gray-700">
                  <FaEnvelope className="text-red-500" />
                  <strong>Email:</strong> info@ambrisservice.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeDown {
          0% {
            opacity: 0;
            transform: translateY(-20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeUp {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-down {
          animation: fadeDown 0.8s ease-out forwards !important;
        }
        .animate-fade-up {
          animation: fadeUp 0.8s ease-out forwards !important;
        }
      `}</style>
    </section>
  );
};

export default PrivacyPolicy;